import {
  ANALYTICS_KEY,
  ACTIVE_MILESTONES,
  BUILD_VERSION,
  detectDeviceClass,
  getBrowser,
  uid,
  type DeviceClass,
  type GameMode,
  type RuleId,
} from './constants';

export type AnalyticsEventName =
  | 'game_page_view'
  | 'play_click'
  | 'load_start'
  | 'playable'
  | 'load_failed'
  | 'first_interaction'
  | 'first_physics_result'
  | 'object_placed'
  | 'rule_changed'
  | 'experiment_run'
  | 'challenge_complete'
  | 'second_experiment_start'
  | 'active_milestone'
  | 'tool_unlocked'
  | 'replay_saved'
  | 'share_complete'
  | 'ad_offer'
  | 'ad_start'
  | 'ad_complete'
  | 'session_end'
  | 'discovery_logged'
  | 'mode_changed'
  | 'undo'
  | 'reset';

export interface AnalyticsProps {
  session_id: string;
  anonymous_id: string;
  build_version: string;
  device_class: DeviceClass;
  browser: string;
  viewport: string;
  mode?: GameMode | string;
  challenge_id?: string;
  run_index?: number;
  tools_used?: string[];
  active_rule?: RuleId | string;
  load_ms?: number;
  active_seconds?: number;
  result_distance?: number;
  result_score?: number;
  ad_exposed?: boolean;
  milestone?: number;
  tool_id?: string;
  discovery_id?: string;
  success?: boolean;
  [key: string]: unknown;
}

export interface AnalyticsEvent {
  name: AnalyticsEventName;
  ts: number;
  props: AnalyticsProps;
}

export interface AnalyticsAdapter {
  track(name: AnalyticsEventName, props?: Partial<AnalyticsProps>): void;
  flush(): void;
  getEvents(): AnalyticsEvent[];
  getSessionSummary(): Record<string, unknown>;
}

const ANON_KEY = 'oddlab_anon_id';

function loadAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = uid('a_');
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return uid('a_');
  }
}

function loadPersisted(): AnalyticsEvent[] {
  try {
    const raw = localStorage.getItem(ANALYTICS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AnalyticsEvent[];
    return Array.isArray(parsed) ? parsed.slice(-500) : [];
  } catch {
    return [];
  }
}

function persist(events: AnalyticsEvent[]) {
  try {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(events.slice(-500)));
  } catch {
    /* quota */
  }
}

export function createLocalAnalytics(): AnalyticsAdapter {
  const session_id = uid('s_');
  const anonymous_id = loadAnonId();
  const events: AnalyticsEvent[] = [];
  const persisted = loadPersisted();
  const firedMilestones = new Set<number>();
  let firstInteraction = false;
  let firstPhysics = false;
  let secondExperiment = false;
  let runIndex = 0;
  let loadStart = performance.now();
  let playableAt = 0;
  let activeMs = 0;
  let lastActiveTick = performance.now();
  let pageVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
  let lastInputAt = performance.now();
  let simRunning = false;
  let adExposed = false;
  let mode: GameMode | string = 'quick';
  let challengeId: string | undefined;
  let activeRule: RuleId | string = 'normal';
  let toolsUsed: string[] = [];

  const baseProps = (): AnalyticsProps => ({
    session_id,
    anonymous_id,
    build_version: BUILD_VERSION,
    device_class: detectDeviceClass(),
    browser: getBrowser(),
    viewport: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`,
    mode,
    challenge_id: challengeId,
    run_index: runIndex,
    tools_used: [...toolsUsed],
    active_rule: activeRule,
    load_ms: playableAt ? Math.round(playableAt - loadStart) : undefined,
    active_seconds: Math.floor(activeMs / 1000),
    ad_exposed: adExposed,
  });

  function isActivelyEngaged(now: number): boolean {
    if (!pageVisible) return false;
    if (simRunning) return true;
    return now - lastInputAt < 15000;
  }

  function tickActive() {
    const now = performance.now();
    if (isActivelyEngaged(now)) {
      activeMs += now - lastActiveTick;
      const secs = Math.floor(activeMs / 1000);
      for (const m of ACTIVE_MILESTONES) {
        if (secs >= m && !firedMilestones.has(m)) {
          firedMilestones.add(m);
          track('active_milestone', { milestone: m, active_seconds: secs });
        }
      }
    }
    lastActiveTick = now;
  }

  const interval =
    typeof window !== 'undefined'
      ? window.setInterval(tickActive, 1000)
      : (0 as unknown as number);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      tickActive();
      pageVisible = document.visibilityState === 'visible';
      lastActiveTick = performance.now();
    });
    const markInput = () => {
      lastInputAt = performance.now();
      if (!firstInteraction && playableAt) {
        firstInteraction = true;
        track('first_interaction');
      }
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach((ev) =>
      window.addEventListener(ev, markInput, { passive: true }),
    );
  }

  function track(name: AnalyticsEventName, props: Partial<AnalyticsProps> = {}) {
    tickActive();
    const event: AnalyticsEvent = {
      name,
      ts: Date.now(),
      props: { ...baseProps(), ...props },
    };
    events.push(event);
    if (events.length % 5 === 0) persist([...persisted, ...events].slice(-500));

    if (typeof window !== 'undefined') {
      const w = window as unknown as { __oddLabAnalytics?: unknown };
      w.__oddLabAnalytics = api;
    }
  }

  const api: AnalyticsAdapter & {
    setMode: (m: GameMode | string) => void;
    setChallenge: (id?: string) => void;
    setRule: (r: RuleId | string) => void;
    setToolsUsed: (t: string[]) => void;
    setSimRunning: (v: boolean) => void;
    markLoadStart: () => void;
    markPlayable: () => void;
    markLoadFailed: (err?: string) => void;
    markPhysicsResult: (distance: number, score?: number) => void;
    markExperimentRun: (distance: number, success: boolean) => void;
    markSecondExperiment: () => void;
    markAdExposed: () => void;
    noteInput: () => void;
    destroy: () => void;
    session_id: string;
    anonymous_id: string;
  } = {
    session_id,
    anonymous_id,
    track,
    flush() {
      persist([...persisted, ...events].slice(-500));
    },
    getEvents() {
      return [...events];
    },
    getSessionSummary() {
      return {
        session_id,
        anonymous_id,
        build_version: BUILD_VERSION,
        event_count: events.length,
        active_seconds: Math.floor(activeMs / 1000),
        run_index: runIndex,
        first_interaction: firstInteraction,
        first_physics: firstPhysics,
        second_experiment: secondExperiment,
        milestones: [...firedMilestones],
        names: events.map((e) => e.name),
      };
    },
    setMode(m) {
      mode = m;
    },
    setChallenge(id) {
      challengeId = id;
    },
    setRule(r) {
      activeRule = r;
    },
    setToolsUsed(t) {
      toolsUsed = t;
    },
    setSimRunning(v) {
      simRunning = v;
      lastInputAt = performance.now();
    },
    markLoadStart() {
      loadStart = performance.now();
      track('load_start');
    },
    markPlayable() {
      playableAt = performance.now();
      track('playable', { load_ms: Math.round(playableAt - loadStart) });
    },
    markLoadFailed(err) {
      track('load_failed', { error: err });
    },
    markPhysicsResult(distance, score) {
      if (!firstPhysics) {
        firstPhysics = true;
        track('first_physics_result', { result_distance: distance, result_score: score });
      }
    },
    markExperimentRun(distance, success) {
      runIndex += 1;
      track('experiment_run', {
        run_index: runIndex,
        result_distance: distance,
        success,
      });
    },
    markSecondExperiment() {
      if (!secondExperiment) {
        secondExperiment = true;
        track('second_experiment_start', { run_index: runIndex });
      }
    },
    markAdExposed() {
      adExposed = true;
    },
    noteInput() {
      lastInputAt = performance.now();
    },
    destroy() {
      if (interval) clearInterval(interval);
      track('session_end', { active_seconds: Math.floor(activeMs / 1000) });
      api.flush();
    },
  };

  if (typeof window !== 'undefined') {
    (window as unknown as { __oddLabAnalytics: typeof api }).__oddLabAnalytics = api;
    window.addEventListener('pagehide', () => api.destroy());
  }

  return api;
}

export type OddLabAnalytics = ReturnType<typeof createLocalAnalytics>;
